import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ModifiedScenarioNamed } from '../../util';
import { IncompleteStateMarkerNew, Poppins, TextOverflowTooltip, getIncompleteStateNew } from 'src/common';
import { Link } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { CompareStatusEdit } from 'src/assets/dynamicSvg';

const Div = styled.div<{ $noBorderTop?: boolean }>`
  min-height: 80px;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: minmax(200px, 600px) minmax(470px, 1500px) minmax(470px, 1500px);
  position: relative;
  width: 100%;

  .item {
    display: flex;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    align-items: center;

    ${({ $noBorderTop }) =>
      !$noBorderTop &&
      css`
        border-top: none;
      `};

    &--name {
      position: relative;
      padding: 0 10px 0 20px;

      .data-changes {
        position: absolute;
        left: 3px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
      }
    }

    &--data {
      padding: 0 29px 0 22px;
      display: grid;
      grid-template-columns: 0.5fr 0.5fr;
      grid-gap: 5px;
    }

    .price {
      position: relative;
      display: flex;
      margin-right: 10px;

      &__incomplete {
        position: absolute;
        right: -20px;
      }
    }
  }

  .pointer {
    cursor: pointer;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    min-height: 61px;
    grid-template-columns: minmax(150px, 600px) minmax(370px, 1500px) minmax(370px, 1500px);
    .item {
      &--name {
        padding: 0 10px;
      }

      &--data {
        padding: 0 10px;
      }
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    min-height: 30px;
    grid-template-columns: minmax(120px, 600px) minmax(260px, 1500px) minmax(260px, 1500px);
  }
`;

interface LabelProps {
  $hasChanges?: boolean;
  $loss?: boolean;
}
const Label = styled(Poppins).attrs((p) => {
  return { px: 14, ...p };
})<LabelProps>`
  font-weight: 500;
  ${({ $hasChanges }) =>
    $hasChanges &&
    css`
      font-weight: 600;
      color: ${({ theme }) => theme.colors.gp1};
    `};

  ${({ $loss }) =>
    $loss &&
    css`
      text-align: right;
    `};
`;

interface RowProps {
  dataA?: ModifiedScenarioNamed;
  dataB?: ModifiedScenarioNamed;
  noBorderTop?: boolean;
  dataChanges?: boolean;
  genPathInner: (id: string, from: 'A' | 'B') => string;
  onScenarioClick?: (scenario: ModifiedScenarioNamed, from: 'A' | 'B') => void;
}

export const Row: React.FC<RowProps> = ({ dataA, dataB, noBorderTop, dataChanges, genPathInner, onScenarioClick }) => {
  const [label, setLabel] = useState<JSX.Element>(<></>);

  useEffect(() => {
    if (!dataA) {
      setLabel(
        <TextOverflowTooltip>
          <Label nowrap>{dataB?.name?.value}</Label>
        </TextOverflowTooltip>,
      );
      return;
    }
    if (!dataB) {
      setLabel(
        <TextOverflowTooltip>
          <Label nowrap>{dataA?.name?.value}</Label>
        </TextOverflowTooltip>,
      );
      return;
    }

    if (dataA.name?.value === dataB.name?.value) {
      setLabel(
        <TextOverflowTooltip>
          <Label nowrap>{dataA.name?.value}</Label>
        </TextOverflowTooltip>,
      );
      return;
    }

    setLabel(
      <>
        <TextOverflowTooltip tooltipText={`${dataA.name?.value} / ${dataB.name?.value}`}>
          <Label nowrap $hasChanges={dataA.name?.hasChanges}>
            {dataA.name?.value} /{' '}
            <Label nowrap $hasChanges={dataB.name?.hasChanges}>
              {` ${dataB.name?.value}`}
            </Label>
          </Label>
        </TextOverflowTooltip>
      </>,
    );
  }, [dataA, dataB]);

  // Create scenario objects for IncompleteStateMarkerNew
  const scenarioA = dataA
    ? {
        id: dataA.id?.value || '',
        isIncomplete: dataA.isIncomplete?.value,
        status: dataA.status?.value,
      }
    : null;

  const scenarioB = dataB
    ? {
        id: dataB.id?.value || '',
        isIncomplete: dataB.isIncomplete?.value,
        status: dataB.status?.value,
      }
    : null;

  // Use the correct GraphQL incomplete state logic
  const isIncompleteA = scenarioA ? getIncompleteStateNew(scenarioA) : null;
  const isIncompleteB = scenarioB ? getIncompleteStateNew(scenarioB) : null;

  return (
    <Div $noBorderTop={noBorderTop}>
      <div
        className="item item--name"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (dataA && onScenarioClick) {
            onScenarioClick(dataA, 'A');
          } else if (dataB && onScenarioClick) {
            onScenarioClick(dataB, 'B');
          }
        }}
      >
        {dataChanges && (
          <div className="data-changes">
            <CompareStatusEdit />
          </div>
        )}
        {label}
      </div>

      {dataA ? (
        <Link
          className={`item item--data ${dataA ? 'pointer' : ''}`}
          to={genPathInner(dataA.referenceId?.value || '', 'A')}
          onClick={() => {
            const path = genPathInner(dataA.referenceId?.value || '', 'A');
            mpEvent(MPEvents.Link, {
              source: { value: ['Compare Assessments'] },
              destination: { value: [path.startsWith('/compare') ? 'Compare Scenarios' : 'Scenario'] },
            });
          }}
        >
          <Label nowrap>
            <TLLink date={dataA.meta?.value?.createdAt || ''}>{formatDate(dataA.meta?.value?.createdAt || '')}</TLLink>
          </Label>
          <div className="price">
            {isIncompleteA === 'TECH' ? (
              <Label>
                <IncompleteStateMarkerNew scenario={scenarioA!} />
              </Label>
            ) : (
              <Label nowrap $loss>
                {numeralFormat(dataA.baseRisk?.value?.expectedLoss || 0)}
              </Label>
            )}

            {isIncompleteA && (
              <div className="price__incomplete">
                <IncompleteStateMarkerNew scenario={scenarioA!} />
              </div>
            )}
          </div>
        </Link>
      ) : (
        <div className="item" />
      )}

      {dataB ? (
        <Link
          className={`item item--data ${dataB ? 'pointer' : ''}`}
          to={genPathInner(dataB.referenceId?.value || '', 'B')}
          onClick={() => {
            const path = genPathInner(dataB.referenceId?.value || '', 'B');
            mpEvent(MPEvents.Link, {
              source: { value: ['Compare Assessments'] },
              destination: { value: [path.startsWith('/compare') ? 'Compare Scenarios' : 'Scenario'] },
            });
          }}
        >
          <Label nowrap $hasChanges={dataB.meta?.hasChanges}>
            <TLLink date={dataB.meta?.value?.createdAt || ''}>{formatDate(dataB.meta?.value?.createdAt || '')}</TLLink>
          </Label>
          <div className="price">
            {isIncompleteB === 'TECH' ? (
              <Label>
                <IncompleteStateMarkerNew scenario={scenarioB!} />
              </Label>
            ) : (
              <Label $loss $hasChanges={dataB.baseRisk?.hasChanges}>
                {numeralFormat(dataB.baseRisk?.value?.expectedLoss || 0)}
              </Label>
            )}

            {isIncompleteB && (
              <div className="price__incomplete">
                <IncompleteStateMarkerNew scenario={scenarioB!} />
              </div>
            )}
          </div>
        </Link>
      ) : (
        <div className="item" />
      )}
    </Div>
  );
};
